/**
 * RecordRow
 *
 * One row in the Records list: selection checkbox, name (falling back to the
 * docId when unnamed), and a status badge only for non-default statuses (Active
 * is implicit, so its tag is omitted to keep the list quiet). A left accent
 * marks the open record. Clicking the row opens the record (detail pane);
 * clicking the checkbox toggles selection (shift+click extends a range). Reads
 * its own selection slice via useSnapshot so only the changed row re-renders.
 */
import { FC, memo, MouseEvent } from 'react';
import { Checkbox, Tag, Typography, theme } from 'antd';
import { useSnapshot } from 'valtio';

import type { EntityRecord } from '#types';
import { RecordStatus } from '#types/System.Parameters.types';
import { recordsBrowserState } from './recordsBrowserState';
import { recordStatusColor } from './recordStatusColor';

const { Text } = Typography;

interface RecordRowProps {
    record: EntityRecord;
    active: boolean;
    onOpen: (recordId: string) => void;
    onToggleSelection: (docId: string, shiftKey: boolean) => void;
}

const RecordRow: FC<RecordRowProps> = ({ record, active, onOpen, onToggleSelection }) => {
    const { token } = theme.useToken();
    const docId = record.docId as string;

    const snap = useSnapshot(recordsBrowserState);
    const checked = snap.selectAllMatching || snap.selection.selectedRows[docId] === true;

    const handleCheckboxClick = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onToggleSelection(docId, e.shiftKey);
    };

    return (
        <div
            role="button"
            aria-label={`record-row-${docId}`}
            onClick={() => onOpen(docId)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                boxSizing: 'border-box',
                padding: '5px 20px',
                cursor: 'pointer',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                borderLeft: `3px solid ${active ? token.colorPrimary : 'transparent'}`,
                backgroundColor: active ? token.colorPrimaryBg : undefined,
            }}
        >
            <span onClick={handleCheckboxClick} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox checked={checked} aria-label={`select-record-${docId}`} />
            </span>
            <Text style={{ flex: 1, minWidth: 0 }}>
                {record.name || docId}
            </Text>
            {record.status && record.status !== RecordStatus.Active && (
                <Tag color={recordStatusColor[record.status]} style={{ marginInlineEnd: 0 }}>
                    {record.status}
                </Tag>
            )}
        </div>
    );
};

export default memo(RecordRow);
